import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import AudioPlayer, { AudioPlayerRef } from '../../components/learning/AudioPlayer';
import { useLearningContext } from './LearningLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useLearningSession } from '../../hooks/useLearningSession';
import { ClickableText, ClickableWord } from '../../components/ClickableWord';
import { AnnotatedText } from '../../components/AnnotatedText';
import { AnnotatedChunks } from '../../components/learning/AnnotatedChunks';
import { DependencyGraph, DependencyGraphData as DependencyGraphComponentData } from '../../components/dependency-graph';
import { dictionaryService, DictionaryWord } from '../../services/dictionaryService';
import { cefrKnowledgeService, CEFRLevel, WordAnnotation } from '../../services/cefrKnowledgeService';
import { learningService } from '../../services/learningService';
import { convertPhonetic, getUserPhoneticSystem } from '../../utils/phoneticConverter';
import { getSharedLemma, getLemmaRelationInfo } from '../../utils/lemmatizer';
import { getTranslation } from '../../utils/getTranslation';
import { LoopyBubble } from '../../components/loopy';
import './ComprehensionPage.css';

import { resolveMediaUrl } from '../../utils/mediaUrl';
import { useKeyboardHeight } from '../../hooks/useKeyboardHeight';
import {
  containsFullWidthAlpha,
  toHalfWidth,
  isPunctuationOnly,
  normalizeWord,
  isWordCorrect,
  getWordMaxLength,
  getLeadingPunctuation,
  getTrailingPunctuation,
  coerceInputCase,
} from '../../utils/wordComparison';
import { useToastStore } from '../../stores/toastStore';
import XpToastContainer, { useXpToast } from '../../components/learning/XpToast';
import LetterPicker from '../../components/learning/LetterPicker';
import InputModeToggle from '../../components/learning/InputModeToggle';
import { useDictationInputMode } from '../../hooks/useDictationInputMode';
import { usePhonicsErrorCollector } from '../../hooks/usePhonicsErrorCollector';
import { usePhonicsGuidance } from '../../hooks/usePhonicsGuidance';
import PhonicsSessionSummary from '../../components/learning/PhonicsSessionSummary';

// ============================================
// Type Definitions
// ============================================

type ComprehensionPhase = 'dictation' | 'understanding' | 'evaluation' | 'complete';

interface Segment {
  id: string;
  text: string;
  translation: string;
  audioUrl?: string;
  startTime: number;
  endTime: number;
  vocabulary?: VocabularyItem[];
  phrases?: PhraseItem[];
  chunks?: ChunkItem[];
  grammarPoints?: GrammarPoint[];
  speakingTip?: string;
  tone?: string;
  dependencyGraph?: DependencyGraphComponentData;
  culturalNotes?: { noteEn: string; noteNative: string };
}

interface VocabularyItem {
  word: string;
  partOfSpeech: string;
  definition: string;
  phonetic?: string;
  audioUrl?: string;
}

interface PhraseItem {
  phrase: string;
  definition: string;
  phonetic?: string;
  audioUrl?: string;
  phraseType?: 'idiom' | 'phrasal_verb' | 'collocation' | 'technical_term';
}

interface ChunkItem {
  text: string;
  meaning: string;
  annotations?: Array<{ word: string; type: 'stress' | 'link' | 'pause' | 'reduction' | 'ipa' | 'syllables'; hint?: string }>;
  intonation?: 'rising' | 'falling' | null;
  pause?: boolean;
}

interface GrammarPoint {
  title: string;
  explanation: string;
}

interface EvaluationResult {
  isCorrect: boolean;
  overallScore: number;
  errors: Array<{
    type: 'vocabulary' | 'grammar' | 'structure';
    description: string;
    relatedWord?: string;
    relatedGrammar?: string;
    highlightNodeIds?: string[];
  }>;
  feedback: string;
  vocabularyUpdated: boolean;
  wordsUpdatedCount: number;
  xpEarned?: number;
  leveledUp?: boolean;
  newLevel?: number;
}

interface WordResult {
  word: string;
  userInput: string;
  isCorrect: boolean;
  sentenceIndex: number;
}

// API response types (snake_case from backend)
interface ApiSpeakingChunk {
  text?: string;
  en?: string;
  translation?: string;
  translations?: Record<string, string> | null;
  meaning?: string;
  zh_tw?: string;
  annotations?: Array<{ word: string; type: 'stress' | 'link' | 'pause' | 'reduction' | 'ipa' | 'syllables'; hint?: string }>;
  intonation?: 'rising' | 'falling' | null;
  pause?: boolean;
}

interface ApiVocabularyItem {
  word?: string;
  pos?: string;
  partOfSpeech?: string;
  definition_native?: string;
  definition?: string;
  pronunciation?: string;
  definition_in_context?: string;
  general_definition?: string;
}

interface ApiGrammarPoint {
  pattern?: string;
  title?: string;
  explanation_native?: string;
  explanation_natives?: Record<string, string> | null;
  explanation?: string;
}

interface ApiPhrase {
  phrase?: string;
  text?: string;
  type?: string;
  definition_native?: string;
  definition_natives?: Record<string, string> | null;
  translation?: string;
  definition?: string;
}

interface ApiSentence {
  index?: number;
  text?: string;
  translation?: string;
  translations?: Record<string, string> | null;
  audio_url?: string;
  start_time?: number;
  end_time?: number;
  speaking_strategy?: { chunks?: ApiSpeakingChunk[]; tips_native?: string; tips_natives?: Record<string, string> | null; tone?: string };
  key_vocabulary_in_sentence?: (string | ApiVocabularyItem)[];
  grammar_points?: ApiGrammarPoint[];
  dependency_graph?: any;
  cultural_notes?: { note_en: string; note_native: string; note_natives?: Record<string, string> | null };
}

// Dependency Graph data from API (snake_case)
interface ApiDependencyWordNode {
  id: string;
  word: string;
  role: string;
  role_native: string;
  role_natives?: Record<string, string> | null;
  color: string;
  form?: string;
  lemma?: string;
  definition_native?: string;
}

interface ApiDependencyArc {
  from: string;
  to: string;
  label: string;
  labels?: Record<string, string> | null;
  label_en?: string;
}

interface ApiDependencyAnimationStep {
  node_ids: string[];
  explanation: string;
  explanations?: Record<string, string> | null;
  duration?: number;
}

interface ApiDependencyGraphData {
  words: ApiDependencyWordNode[];
  arcs: ApiDependencyArc[];
  animation_steps: ApiDependencyAnimationStep[];
}

// ============================================
// Helper Functions
// ============================================

// Transform API dependency graph data (snake_case) to component format (camelCase)
const transformDependencyGraph = (apiData: ApiDependencyGraphData, lang: string): DependencyGraphComponentData | null => {
  if (!apiData.words || !Array.isArray(apiData.words) ||
      !apiData.arcs || !Array.isArray(apiData.arcs) ||
      !apiData.animation_steps || !Array.isArray(apiData.animation_steps)) {
    return null;
  }

  return {
    sentence: '',
    words: apiData.words.map(w => ({
      id: w.id,
      word: w.word,
      role: w.role,
      roleZh: getTranslation(w.role_natives, lang, w.role_native),
      color: w.color,
      lemma: w.lemma,
      form: w.form,
      definitionZh: w.definition_native,
    })),
    arcs: apiData.arcs.map(a => ({
      from: a.from,
      to: a.to,
      label: getTranslation(a.labels, lang, a.label),
      labelEn: a.label_en,
    })),
    animationSteps: apiData.animation_steps.map(s => ({
      nodeIds: s.node_ids,
      explanation: getTranslation(s.explanations, lang, s.explanation),
      duration: s.duration,
    })),
  };
};


// ============================================
// VocabularyCard Component
// ============================================

interface VocabularyCardProps {
  word: string;
  partOfSpeech?: string;
  definition: string;
  phonetic?: string;
  audioUrl?: string;
  type: 'word' | 'phrase';
  phraseType?: 'idiom' | 'phrasal_verb' | 'collocation' | 'technical_term';
  cefrLevel?: CEFRLevel;
  onAddNote: () => void;
}

const VocabularyCard: React.FC<VocabularyCardProps> = ({
  word,
  partOfSpeech,
  definition,
  phonetic,
  audioUrl,
  type,
  phraseType,
  cefrLevel,
  onAddNote
}) => {
  const { t } = useTranslation('learning');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedPhonetic, setLoadedPhonetic] = useState<string | null>(null);
  const [loadedDefinition, setLoadedDefinition] = useState<string | null>(null);
  const [loadedPos, setLoadedPos] = useState<string | null>(null);
  const [loadedAudioUrl, setLoadedAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const phoneticSystem = useMemo(() => getUserPhoneticSystem(), []);

  useEffect(() => {
    if (type === 'word') {
      dictionaryService.lookup(word, { generateAudio: true }).then((result: DictionaryWord) => {
        if (result) {
          setLoadedPhonetic(result.phonetic || null);
          setLoadedDefinition(result.translation || result.definition || null);
          setLoadedPos(result.pos || null);
          if (result.audio && result.audio.length > 0) {
            setLoadedAudioUrl(result.audio[0].url);
          }
        }
      }).catch((err) => {
        // Silently ignore dictionary lookup failures - word may not exist in dictionary
        console.debug('Dictionary lookup failed for word:', word, err);
      });
    }
  }, [word, type]);

  const isValidPos = (pos: string | null | undefined): boolean => {
    if (!pos) return false;
    const validPosPatterns = /^(n\.|v\.|adj\.|adv\.|prep\.|conj\.|pron\.|art\.|int\.|noun|verb|adjective|adverb|preposition|conjunction|pronoun|article|interjection|other)$/i;
    return validPosPatterns.test(pos.trim());
  };

  const displayPhonetic = loadedPhonetic || phonetic;
  const displayDefinition = definition || loadedDefinition || '';
  const displayPos = isValidPos(partOfSpeech) ? partOfSpeech : (isValidPos(loadedPos) ? loadedPos : '');
  const displayAudioUrl = loadedAudioUrl || audioUrl;

  const handlePlayAudio = async () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    if (displayAudioUrl) {
      if (audioRef.current) {
        audioRef.current.src = resolveMediaUrl(displayAudioUrl);
        audioRef.current.play();
        setIsPlaying(true);
      }
    } else {
      setIsLoading(true);
      try {
        const result = await dictionaryService.lookup(word, { generateAudio: true });
        if (result?.audio && result.audio.length > 0) {
          const newAudioUrl = result.audio[0].url;
          setLoadedAudioUrl(newAudioUrl);
          if (audioRef.current) {
            audioRef.current.src = newAudioUrl;
            audioRef.current.play();
            setIsPlaying(true);
          }
        }
      } catch (error) {
        console.error('Failed to load audio:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className={`vocabulary-item vocabulary-item--${type}`}>
      <div className="vocabulary-item__header">
        <span className="vocabulary-item__word">
          <ClickableWord word={word} />
        </span>
        {type === 'phrase' && (
          <span className={`vocabulary-item__type-badge ${phraseType === 'technical_term' ? 'vocabulary-item__type-badge--technical' : ''}`}>
            {phraseType === 'technical_term' ? t('comprehensionPage.technicalTermTag', { defaultValue: '專業術語' })
              : phraseType === 'idiom' ? t('comprehensionPage.idiomTag', { defaultValue: '慣用語' })
              : phraseType === 'phrasal_verb' ? t('comprehensionPage.phrasalVerbTag', { defaultValue: '片語動詞' })
              : t('comprehensionPage.phraseTag')}
          </span>
        )}
        {displayPos && <span className="vocabulary-item__pos">{displayPos}</span>}
        {cefrLevel && (
          <span className={`vocabulary-item__cefr-badge vocabulary-item__cefr-badge--${cefrLevel}`}>
            {cefrLevel}
          </span>
        )}
        <button
          className={`vocabulary-item__audio-btn ${isPlaying ? 'playing' : ''} ${isLoading ? 'loading' : ''}`}
          onClick={handlePlayAudio}
          disabled={isLoading}
        >
          {isLoading ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="3" opacity="0.3" />
            </svg>
          ) : isPlaying ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
          )}
        </button>
      </div>
      {displayPhonetic && (
        <div className="vocabulary-item__phonetic">/{convertPhonetic(displayPhonetic, phoneticSystem)}/</div>
      )}
      {displayDefinition && <div className="vocabulary-item__definition">{displayDefinition}</div>}
      <button className="vocabulary-item__note-btn" onClick={onAddNote}>{t('comprehensionPage.noteBtn')}</button>
      <audio ref={audioRef} onEnded={() => setIsPlaying(false)} preload="none" />
    </div>
  );
};

// ============================================
// Main Component
// ============================================

const ComprehensionPage: React.FC = () => {
  const { t, i18n } = useTranslation(['learning', 'common']);
  const { courseData, loading, error, setCurrentSegmentContext, openNoteWithContent } = useLearningContext();
  const { user } = useAuth();
  const { toasts: xpToasts, showXp } = useXpToast();
  const userLang = user?.languagePreference || courseData?.course?.translationLanguage || 'zh-TW';
  const [searchParams] = useSearchParams();
  // Used by deep-links from notifications (e.g. "creator answered your
  // question") to land directly on the relevant sentence.
  const deepLinkSegmentId = searchParams.get('segment');

  // Phase management
  const [phase, setPhase] = useState<ComprehensionPhase>('understanding');
  const [currentIndex, setCurrentIndex] = useState(0);
  const deepLinkAppliedRef = useRef(false);

  // Dictation phase state
  const [wordInputs, setWordInputs] = useState<string[]>([]);
  const [activeWordIndex, setActiveWordIndex] = useState(0);
  const [dictationValidated, setDictationValidated] = useState(false);
  const [audioSpeed, setAudioSpeed] = useState<number | undefined>(undefined);
  const [audioRepeating, setAudioRepeating] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const audioPlayerRef = useRef<AudioPlayerRef>(null);
  const { isLetterPicker, toggle: toggleInputMode } = useDictationInputMode();
  const [letterPickerResetKey, setLetterPickerResetKey] = useState(0);
  const { handleLetterError, getSessionErrors } = usePhonicsGuidance();

  // Understanding phase state
  const [userTranslation, setUserTranslation] = useState('');

  // Evaluation phase state
  const [evaluationResult, setEvaluationResult] = useState<EvaluationResult | null>(null);
  const [evaluationLoading, setEvaluationLoading] = useState(false);

  const [isTranslationFocused, setIsTranslationFocused] = useState(false);
  const { isKeyboardOpen } = useKeyboardHeight();

  useEffect(() => {
    const cls = 'comprehension-immersive';
    if (isTranslationFocused) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [isTranslationFocused]);

  // iOS natively scrolls the focused textarea into view before React can
  // apply the overflow:hidden lock, so the scroll offset is already set
  // when we freeze the container — leaving the sentence-card above the
  // fold. Reset scrollTop on every scroll ancestor right after focus, and
  // again after a tick, to undo iOS's scroll-into-view.
  useEffect(() => {
    if (!isTranslationFocused) return;
    const reset = () => {
      document.querySelectorAll<HTMLElement>(
        '.comprehension-content, .learning-layout__main',
      ).forEach((el) => { el.scrollTop = 0; });
      document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    };
    reset();
    const raf = window.requestAnimationFrame(reset);
    const t = window.setTimeout(reset, 200);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, [isTranslationFocused]);

  // iOS's hide-keyboard button dismisses the virtual keyboard without firing
  // blur on the textarea, so onBlur alone can't restore the layout. Track
  // whether the keyboard has opened during this focus session, and when it
  // transitions from open -> closed while still focused, treat it as a
  // dismissal (exit immersive mode and blur the textarea so the next tap
  // properly reopens the keyboard).
  const keyboardWasOpenRef = useRef(false);
  useEffect(() => {
    if (!isTranslationFocused) {
      keyboardWasOpenRef.current = false;
      return;
    }
    if (isKeyboardOpen) {
      keyboardWasOpenRef.current = true;
      return;
    }
    if (keyboardWasOpenRef.current) {
      keyboardWasOpenRef.current = false;
      setIsTranslationFocused(false);
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    }
  }, [isTranslationFocused, isKeyboardOpen]);

  // Reference panel state
  const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);
  const [graphZoom, setGraphZoom] = useState(1);

  // CEFR level mapping
  const [cefrLevelMap, setCefrLevelMap] = useState<Record<string, CEFRLevel>>({});
  const [cefrGrammarMap, setCefrGrammarMap] = useState<Record<string, { cefrLevel: CEFRLevel; displayName: string }>>({});
  const [annotationsBySegment, setAnnotationsBySegment] = useState<Record<string, WordAnnotation[]>>({});

  // Progress tracking
  const [progressLoaded, setProgressLoaded] = useState(false);
  const isInitialMount = useRef(true);
  const fullWidthWarningRef = useRef<number>(0);

  // Lemma modal state
  const [lemmaModalData, setLemmaModalData] = useState<{
    userWord: string;
    correctWord: string;
    explanation: string;
    userForm?: string;
    correctForm?: string;
  } | null>(null);

  // Learning session tracking
  const courseId = courseData?.course?.id;
  const { addErrors: addPhonicsErrors } = usePhonicsErrorCollector(courseId, 'comprehension');
  // Track LetterPicker revealed positions so scoring penalizes hinted letters
  const lpRevealedRef = useRef<Set<number>>(new Set());
  const handleLpErrorData = useCallback((data: import('../../components/learning/LetterPicker').LetterPickerErrorData) => {
    if (data.revealedPositions.length > 0) {
      data.revealedPositions.forEach(p => lpRevealedRef.current.add(p));
    }
    addPhonicsErrors(data);
  }, [addPhonicsErrors]);
  const { updateContext: updateSessionContext } = useLearningSession({
    courseId: courseId || '',
    mode: 'comprehension',
    enabled: !!courseId && !!user,
    contextData: { sentenceIndex: currentIndex },
  });

  useEffect(() => {
    updateSessionContext({ sentenceIndex: currentIndex });
  }, [currentIndex, updateSessionContext]);

  // Load CEFR data
  useEffect(() => {
    let isMounted = true;
    const loadCefrData = async () => {
      if (!courseData?.course?.id) return;
      try {
        const [stats, annotationsData] = await Promise.all([
          cefrKnowledgeService.getCourseKnowledgePointStats(courseData.course.id),
          cefrKnowledgeService.getCourseAnnotations(courseData.course.id),
        ]);

        if (isMounted) {
          const levelMap: Record<string, CEFRLevel> = {};
          const grammarMap: Record<string, { cefrLevel: CEFRLevel; displayName: string }> = {};

          for (const kp of stats.knowledgePoints) {
            if (kp.category === 'vocabulary' || kp.category === 'phrasal_verb') {
              levelMap[kp.term.toLowerCase()] = kp.cefrLevel;
            } else if (kp.category === 'grammar') {
              const parts = kp.term.split(' > ');
              const displayName = parts[parts.length - 1]
                .replace(/_/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase());
              grammarMap[kp.term.toLowerCase()] = { cefrLevel: kp.cefrLevel, displayName };
            }
          }
          setCefrLevelMap(levelMap);
          setCefrGrammarMap(grammarMap);
          setAnnotationsBySegment(annotationsData.annotations);
        }
      } catch (error) {
        console.error('Failed to load CEFR data:', error);
      }
    };
    loadCefrData();
    return () => { isMounted = false; };
  }, [courseData?.course?.id]);

  // Load progress on mount
  useEffect(() => {
    let isMounted = true;
    const loadProgress = async () => {
      if (!courseData?.course?.id) return;
      try {
        const progressSummary = await learningService.getProgressSummary(courseData.course.id);
        if (isMounted) {
          // Deep link wins over saved progress: if the learner clicked a
          // notification with ?segment=, the deep-link effect will set the
          // index — don't clobber it with their previous bookmark.
          const savedIndex = progressSummary.comprehension?.currentIndex;
          if (!deepLinkSegmentId && savedIndex !== undefined && savedIndex >= 0) {
            setCurrentIndex(savedIndex);
          }
          setProgressLoaded(true);
        }
      } catch (error) {
        console.error('Failed to load comprehension progress:', error);
        if (isMounted) {
          setProgressLoaded(true);
        }
      }
    };
    loadProgress();
    return () => { isMounted = false; };
  }, [courseData?.course?.id]);

  // Save position when currentIndex changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!progressLoaded || !courseData?.course?.id) return;

    const savePosition = async () => {
      try {
        await learningService.savePosition(courseData.course.id, 'comprehension', currentIndex);
      } catch (error) {
        console.error('Failed to save comprehension position:', error);
      }
    };
    savePosition();
  }, [currentIndex, courseData?.course?.id, progressLoaded]);

  // Transform API data to component format
  const segments: Segment[] = useMemo(() => {
    if (!courseData?.sentences || courseData.sentences.length === 0) {
      return [];
    }

    return courseData.sentences.map((sentence: ApiSentence, index: number) => {
      const chunks: ChunkItem[] = (sentence.speaking_strategy?.chunks || []).map((chunk: ApiSpeakingChunk) => ({
        text: chunk.text || chunk.en || '',
        meaning: getTranslation(chunk.translations, userLang, chunk.translation || chunk.meaning || chunk.zh_tw || ''),
        annotations: chunk.annotations || undefined,
        intonation: chunk.intonation || undefined,
        pause: chunk.pause || undefined,
      }));

      const keyVocab = sentence.key_vocabulary_in_sentence || [];
      const vocabulary: VocabularyItem[] = keyVocab.map((vocab: string | ApiVocabularyItem) => {
        if (typeof vocab === 'string') {
          const globalVocab = (courseData?.comprehensiveVocabulary || []).find(
            (v: ApiVocabularyItem) => v.word?.toLowerCase() === vocab.toLowerCase()
          );
          return {
            word: vocab,
            partOfSpeech: globalVocab?.pos || '',
            definition: globalVocab?.definition_in_context || globalVocab?.general_definition || '',
            phonetic: globalVocab?.pronunciation || ''
          };
        }
        return {
          word: vocab.word || '',
          partOfSpeech: vocab.pos || vocab.partOfSpeech || '',
          definition: vocab.definition_native || vocab.definition || '',
          phonetic: vocab.pronunciation || ''
        };
      });

      const sentenceTextLower = (sentence.text || '').toLowerCase();
      const allPhrases: ApiPhrase[] = courseData?.phrases || [];
      const phrases: PhraseItem[] = allPhrases
        .filter((p: ApiPhrase) => {
          const phraseText = (p.phrase || p.text || '').toLowerCase();
          return phraseText && sentenceTextLower.includes(phraseText);
        })
        .map((p: ApiPhrase) => ({
          phrase: p.phrase || p.text || '',
          definition: getTranslation(p.definition_natives, userLang, p.definition_native || p.translation || p.definition || ''),
          phraseType: p.type as PhraseItem['phraseType'],
        }));

      const grammarPoints: GrammarPoint[] = (sentence.grammar_points || [])
        .map((gp: ApiGrammarPoint) => ({
          title: gp.pattern || gp.title || '',
          explanation: getTranslation(gp.explanation_natives, userLang, gp.explanation_native || gp.explanation || '')
        }))
        .filter((gp: GrammarPoint) => gp.title.trim() !== '');

      const dependencyGraph: DependencyGraphComponentData | undefined = sentence.dependency_graph
        ? (transformDependencyGraph(sentence.dependency_graph as ApiDependencyGraphData, userLang) ?? undefined)
        : undefined;

      return {
        id: String(sentence.index ?? index),
        text: sentence.text || '',
        translation: getTranslation(sentence.translations, userLang, sentence.translation || ''),
        audioUrl: resolveMediaUrl(sentence.audio_url),
        startTime: (sentence.start_time || 0) * 1000,
        endTime: (sentence.end_time || 0) * 1000,
        vocabulary,
        phrases,
        chunks,
        grammarPoints,
        speakingTip: getTranslation(sentence.speaking_strategy?.tips_natives, userLang, sentence.speaking_strategy?.tips_native || ''),
        tone: sentence.speaking_strategy?.tone || undefined,
        dependencyGraph,
        culturalNotes: sentence.cultural_notes
          ? { noteEn: sentence.cultural_notes.note_en, noteNative: getTranslation(sentence.cultural_notes.note_natives, userLang, sentence.cultural_notes.note_native) }
          : undefined,
      };
    });
  }, [courseData]);

  const currentSegment = segments[currentIndex];

  // Get correct words for dictation
  const correctWords = useMemo(() => {
    if (!currentSegment) return [];
    const rawWords = currentSegment.text.split(/\s+/).filter(w => w.length > 0);
    const expandedWords: string[] = [];
    for (const word of rawWords) {
      if (word.includes('-') && !word.startsWith('-') && !word.endsWith('-')) {
        const parts = word.split('-');
        parts.forEach((part, idx) => {
          if (idx < parts.length - 1) {
            expandedWords.push(part + '-');
          } else {
            expandedWords.push(part);
          }
        });
      } else {
        expandedWords.push(word);
      }
    }
    return expandedWords;
  }, [currentSegment]);

  // Get segment ID mapping
  const segmentIdsByIndex = useMemo((): string[] => {
    if (!courseData?.sections) return [];
    const ids: string[] = [];
    for (const section of courseData.sections) {
      for (const episode of section.episodes || []) {
        for (const segment of episode.segments || []) {
          ids.push(segment.id);
        }
      }
    }
    return ids;
  }, [courseData?.sections]);

  // Deep-link: if URL has ?segment=<uuid>, jump to that sentence on first
  // load. Runs after segmentIdsByIndex is ready and only once per mount so
  // it doesn't fight the user's manual navigation later in the session.
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    if (!deepLinkSegmentId || segmentIdsByIndex.length === 0) return;
    const idx = segmentIdsByIndex.indexOf(deepLinkSegmentId);
    if (idx >= 0) {
      setCurrentIndex(idx);
      deepLinkAppliedRef.current = true;
    }
  }, [deepLinkSegmentId, segmentIdsByIndex]);

  // Get annotations for current segment
  const currentAnnotations = useMemo((): WordAnnotation[] => {
    const segmentId = segmentIdsByIndex[currentIndex];
    if (!segmentId) return [];
    return annotationsBySegment[segmentId] || [];
  }, [segmentIdsByIndex, currentIndex, annotationsBySegment]);

  // Initialize word inputs when segment or phase changes
  useEffect(() => {
    if (phase === 'dictation' && correctWords.length > 0) {
      setWordInputs(new Array(correctWords.length).fill(''));
      // Start focus on first inputable word (skip punctuation-only tokens like "…", "—")
      const firstInputIdx = correctWords.findIndex(w => !isPunctuationOnly(w));
      setActiveWordIndex(firstInputIdx >= 0 ? firstInputIdx : 0);
      inputRefs.current = new Array(correctWords.length).fill(null);
      setDictationValidated(false);
    }
  }, [correctWords.length, currentIndex, phase]);

  // Focus active input in dictation phase
  useEffect(() => {
    if (phase === 'dictation' && !dictationValidated && inputRefs.current[activeWordIndex]) {
      inputRefs.current[activeWordIndex]?.focus();
    }
  }, [activeWordIndex, dictationValidated, phase]);

  // Update context for AI Assistant
  useEffect(() => {
    if (currentSegment) {
      setCurrentSegmentContext({
        segmentId: segmentIdsByIndex[currentIndex],
        sentence: currentSegment.text,
        translation: currentSegment.translation,
        vocabulary: currentSegment.vocabulary?.map(v => v.word),
        grammarPoints: currentSegment.grammarPoints,
        chunks: currentSegment.chunks,
      });
    }
    return () => {
      setCurrentSegmentContext(null);
    };
  }, [currentSegment, currentIndex, segmentIdsByIndex, setCurrentSegmentContext]);

  // ============================================
  // Dictation Phase Handlers
  // ============================================

  const handleWordChange = (index: number, value: string) => {
    // Detect full-width characters and warn user
    if (containsFullWidthAlpha(value)) {
      const now = Date.now();
      if (now - fullWidthWarningRef.current > 3000) {
        fullWidthWarningRef.current = now;
        useToastStore.getState().warning(t('dictation.fullWidthWarning'), 3000);
      }
      value = toHalfWidth(value);
    }

    const cleanValue = value.replace(/\s/g, '');
    const casedValue = coerceInputCase(cleanValue, correctWords[index] || '');
    const maxLen = getWordMaxLength(correctWords[index]);
    const truncatedValue = casedValue.slice(0, maxLen);

    const newInputs = [...wordInputs];
    newInputs[index] = truncatedValue;
    setWordInputs(newInputs);

    if (truncatedValue.length >= maxLen && index < correctWords.length - 1) {
      setTimeout(() => {
        let nextIdx = index + 1;
        while (nextIdx < correctWords.length && isPunctuationOnly(correctWords[nextIdx])) {
          nextIdx++;
        }
        if (nextIdx < correctWords.length) {
          setActiveWordIndex(nextIdx);
        }
      }, 100);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = wordInputs[index];

    if (e.key === ' ') {
      e.preventDefault();
      audioPlayerRef.current?.togglePlayPause();
    }

    if (e.key === 'Backspace' && input === '' && index > 0) {
      e.preventDefault();
      let prevIdx = index - 1;
      while (prevIdx >= 0 && isPunctuationOnly(correctWords[prevIdx])) {
        prevIdx--;
      }
      if (prevIdx >= 0) setActiveWordIndex(prevIdx);
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (dictationValidated) {
        handleComplete();
      } else {
        handleDictationSubmit();
      }
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        let prevIdx = index - 1;
        while (prevIdx >= 0 && isPunctuationOnly(correctWords[prevIdx])) {
          prevIdx--;
        }
        if (prevIdx >= 0) setActiveWordIndex(prevIdx);
      } else {
        let nextIdx = index + 1;
        while (nextIdx < correctWords.length && isPunctuationOnly(correctWords[nextIdx])) {
          nextIdx++;
        }
        if (nextIdx < correctWords.length) setActiveWordIndex(nextIdx);
      }
    }
  };

  const handleDictationSubmit = async (overrideInputs?: string[]) => {
    if (!currentSegment || !courseId) return;
    setDictationValidated(true);

    // Use overrideInputs when the caller has fresh inputs that haven't flushed
    // through state yet (e.g. LetterPicker completion dispatches setWordInputs
    // and immediately schedules a submit via setTimeout — the setTimeout
    // closure would otherwise read the pre-update `wordInputs` and mark every
    // word wrong).
    const inputsToUse = overrideInputs ?? wordInputs;

    // Save dictation results (same as DictationPage), excluding punctuation-only tokens
    const wordResults: WordResult[] = correctWords.map((word, idx) => ({
      word,
      userInput: inputsToUse[idx] || '',
      isCorrect: isWordCorrect(inputsToUse[idx] || '', word),
      sentenceIndex: currentIndex,
    })).filter(wr => !isPunctuationOnly(wr.word));

    try {
      await learningService.saveDictationResults({
        courseId,
        results: wordResults.map(wr => ({
          word: wr.word,
          isCorrect: wr.isCorrect,
          sentenceIndex: wr.sentenceIndex,
        })),
      });
    } catch (err) {
      console.error('Failed to save dictation results:', err);
    }

    // Award XP for dictation phase (comprehension_dictation)
    try {
      const inputableWords = correctWords.filter(w => !isPunctuationOnly(w));
      const correctCount = wordResults.filter(wr => wr.isCorrect).length;
      const accuracy = inputableWords.length > 0 ? Math.round((correctCount / inputableWords.length) * 100) : 100;

      const result = await learningService.completeSentence(
        courseId,
        'comprehension',
        currentIndex,
        accuracy,
        segments.length
      );
      if (result.xpEarned > 0) {
        showXp(result.xpEarned, result.leveledUp, result.newLevel);
      }
    } catch (err) {
      console.error('Failed to save comprehension dictation progress:', err);
    }
  };

  const handleLetterPickerComplete = useCallback((_userText: string) => {
    if (!currentSegment) return;
    const revealed = lpRevealedRef.current;

    // Map character positions in expectedText back to word indices
    const wordsWithRevealed = new Set<number>();
    if (revealed.size > 0) {
      const text = currentSegment.text;
      let charIdx = 0;
      let wordIdx = 0;
      for (let i = 0; i < text.length; i++) {
        if (text[i] === ' ') {
          wordIdx++;
        } else {
          if (revealed.has(charIdx)) {
            wordsWithRevealed.add(wordIdx);
          }
        }
        charIdx++;
      }
    }

    const newInputs = correctWords.map((cw, idx) => {
      if (isPunctuationOnly(cw)) return '';
      if (wordsWithRevealed.has(idx)) return '';
      return normalizeWord(cw);
    });
    setWordInputs(newInputs);
    lpRevealedRef.current = new Set();
    // Pass newInputs directly — setWordInputs hasn't flushed, and the setTimeout
    // closure would otherwise read a stale empty `wordInputs` via handleDictationSubmit.
    setTimeout(() => handleDictationSubmit(newInputs), 300);
  }, [currentSegment, correctWords, normalizeWord, handleDictationSubmit]);

  const handleContinueToDictation = () => {
    setPhase('dictation');
  };

  // ============================================
  // Understanding Phase Handlers
  // ============================================

  const handleTranslationSubmit = async () => {
    if (!currentSegment || !courseId || !userTranslation.trim()) return;

    setEvaluationLoading(true);
    setPhase('evaluation');

    try {
      const context: {
        vocabulary?: Array<{ word: string; definition: string }>;
        grammarPoints?: Array<{ title: string; explanation: string }>;
      } = {};

      if (currentSegment.vocabulary && currentSegment.vocabulary.length > 0) {
        context.vocabulary = currentSegment.vocabulary.map(v => ({
          word: v.word,
          definition: v.definition,
        }));
      }

      if (currentSegment.grammarPoints && currentSegment.grammarPoints.length > 0) {
        context.grammarPoints = currentSegment.grammarPoints.map(g => ({
          title: g.title,
          explanation: g.explanation,
        }));
      }

      // Build surrounding context for AI evaluation (course title + nearby sentences)
      const nearbyTexts: string[] = [];
      for (let i = Math.max(0, currentIndex - 2); i <= Math.min(segments.length - 1, currentIndex + 2); i++) {
        if (i !== currentIndex && segments[i]?.text) {
          nearbyTexts.push(segments[i].text);
        }
      }
      const surroundingContext = [
        courseData?.course?.title ? `Title: ${courseData.course.title}` : '',
        ...nearbyTexts,
      ].filter(Boolean).join(' | ');

      const result = await learningService.evaluateTranslation({
        courseId,
        sentenceIndex: currentIndex,
        originalSentence: currentSegment.text,
        correctTranslation: currentSegment.translation,
        userTranslation: userTranslation.trim(),
        locale: i18n.language,
        ...(Object.keys(context).length > 0 ? { context } : {}),
        ...(surroundingContext ? { surroundingContext } : {}),
      });

      setEvaluationResult(result);

      // Show XP toast for translation evaluation
      if (result.xpEarned > 0) {
        showXp(result.xpEarned, result.leveledUp, result.newLevel);
      }
    } catch (err) {
      console.error('Failed to evaluate translation:', err);
      setEvaluationResult({
        isCorrect: false,
        overallScore: 0,
        errors: [{ type: 'structure', description: '評估失敗，請稍後再試' }],
        feedback: '系統暫時無法評估，請稍後再試。',
        vocabularyUpdated: false,
        wordsUpdatedCount: 0,
      });
    } finally {
      setEvaluationLoading(false);
    }
  };

  // ============================================
  // Phase Handlers
  // ============================================

  const handleComplete = async () => {
    setPhase('complete');

    // Save progress - mark this sentence as completed with score
    if (courseId && evaluationResult) {
      try {
        await learningService.completeSentence(
          courseId,
          'comprehension',
          currentIndex,
          evaluationResult.overallScore,
          segments.length
        );
        // XP already awarded: dictation phase in handleDictationSubmit, translation phase in evaluateTranslation
        console.log(`Comprehension progress saved: sentence ${currentIndex} completed with score ${evaluationResult.overallScore}`);
      } catch (error) {
        console.error('Failed to save comprehension progress:', error);
      }
    }
  };

  const handleNextSentence = () => {
    if (currentIndex < segments.length - 1) {
      setCurrentIndex(currentIndex + 1);
      resetState();
    }
  };

  const handlePreviousSentence = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      resetState();
    }
  };

  const resetState = () => {
    setPhase('understanding');
    setWordInputs([]);
    setActiveWordIndex(0);
    setDictationValidated(false);
    setUserTranslation('');
    setEvaluationResult(null);
    setEvaluationLoading(false);
    setLetterPickerResetKey(k => k + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Global keyboard handler: Enter to continue in evaluation/complete/dictation-validated phases
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      // Skip if typing in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (phase === 'evaluation' && evaluationResult && !evaluationLoading) {
        e.preventDefault();
        handleContinueToDictation();
      } else if (phase === 'dictation' && dictationValidated) {
        e.preventDefault();
        handleComplete();
      } else if (phase === 'complete') {
        e.preventDefault();
        const isLast = currentIndex === segments.length - 1;
        if (!isLast) {
          handleNextSentence();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    // handleComplete and handleNextSentence are plain functions (not useCallback) that depend on
    // state already tracked in deps; adding them would cause infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, evaluationResult, evaluationLoading, currentIndex, segments.length, dictationValidated]);

  const handleAddNote = (type: 'vocabulary' | 'phrase' | 'grammar', content: string, definition?: string) => {
    const segmentId = segmentIdsByIndex[currentIndex];
    const prefix = type === 'grammar' ? t('comprehensionPage.notePrefix.grammar') : type === 'phrase' ? t('comprehensionPage.notePrefix.phrase') : t('comprehensionPage.notePrefix.word');
    const noteContent = definition
      ? `[${prefix}] ${content}\n${definition}`
      : `[${prefix}] ${content}`;
    openNoteWithContent({
      content: noteContent,
      relatedWord: type !== 'grammar' ? content : undefined,
      tags: [type],
      segmentId: segmentId || undefined,
      sentenceIndex: currentIndex,
    });
  };

  // Calculate dictation accuracy (excluding punctuation-only tokens)
  const calculateDictationAccuracy = useCallback((): number => {
    const inputableWords = correctWords.filter(w => !isPunctuationOnly(w));
    if (inputableWords.length === 0) return 100;
    const correctCount = wordInputs.filter((input, idx) =>
      !isPunctuationOnly(correctWords[idx]) && isWordCorrect(input, correctWords[idx])
    ).length;
    return Math.round((correctCount / inputableWords.length) * 100);
  }, [wordInputs, correctWords]);

  // Lemma explanations for dictation
  const lemmaExplanations = useMemo(() => {
    if (!dictationValidated) return new Map<number, { explanation: string; userForm: string; correctForm: string }>();

    const explanations = new Map<number, { explanation: string; userForm: string; correctForm: string }>();
    correctWords.forEach((correctWord, idx) => {
      if (isPunctuationOnly(correctWord)) return; // Skip punctuation-only tokens
      const userInput = wordInputs[idx] || '';
      if (userInput && !isWordCorrect(userInput, correctWord)) {
        const normalizedUser = normalizeWord(userInput);
        const normalizedCorrect = normalizeWord(correctWord);

        if (normalizedUser.toLowerCase() === normalizedCorrect.toLowerCase()) {
          return;
        }

        const sharedLemma = getSharedLemma(normalizedUser, normalizedCorrect);
        if (sharedLemma) {
          // Look up the word's form and role from the dependency graph
          const graphWords = currentSegment?.dependencyGraph?.words;
          const wordNode = graphWords?.find(
            w => w.word.toLowerCase() === normalizedCorrect.toLowerCase()
          );
          const info = getLemmaRelationInfo(normalizedUser, normalizedCorrect, sharedLemma, wordNode?.role, wordNode?.form);
          const explanation = t(`dictation.lemmaHint.${info.translationKey}`, {
            defaultValue: `${info.userWord} ≠ ${info.correctWord}`,
            userWord: info.userWord,
            correctWord: info.correctWord,
            lemma: info.lemma,
          });
          explanations.set(idx, {
            explanation,
            userForm: info.userForm,
            correctForm: info.correctForm,
          });
        }
      }
    });
    return explanations;
  }, [dictationValidated, wordInputs, correctWords, t, currentSegment?.dependencyGraph?.words]);

  // ============================================
  // Render Functions
  // ============================================

  const renderWordInputs = () => {
    return (
      <div className="comprehension-dictation__inputs">
        {correctWords.map((correctWord, idx) => {
          // Punctuation-only token (e.g., "…", "—", "–") — display as static text, not an input
          if (isPunctuationOnly(correctWord)) {
            return (
              <span key={idx} className="word-punctuation-display">{correctWord}</span>
            );
          }

          const userInput = wordInputs[idx] || '';
          const isEmpty = userInput === '';
          const isActive = idx === activeWordIndex;
          const correct = isWordCorrect(userInput, correctWord);
          const lemmaExplanation = lemmaExplanations.get(idx);

          const charWidth = 14;
          const padding = 32;   // account for CSS padding (24px) + border (4px) + buffer
          const normalizedLen = normalizeWord(correctWord).length;
          const minWidth = Math.max(normalizedLen * charWidth + padding, 60);
          const maxLen = getWordMaxLength(correctWord);

          const leadingPunct = getLeadingPunctuation(correctWord);
          const trailingPunct = getTrailingPunctuation(correctWord);

          const inputClassName = `word-input ${isActive ? 'word-input--active' : ''} ${dictationValidated && correct ? 'word-input--correct' : ''} ${dictationValidated && !correct && !isEmpty ? 'word-input--incorrect' : ''}`;

          return (
            <div key={idx} className="word-input-wrapper">
              {leadingPunct && <span className="word-punctuation word-punctuation--leading">{leadingPunct}</span>}
              <input
                ref={(el) => { inputRefs.current[idx] = el; }}
                type="text"
                name={`comprehension-word-${currentIndex}-${idx}`}
                className={inputClassName}
                value={userInput}
                onChange={(e) => handleWordChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                onFocus={() => setActiveWordIndex(idx)}
                disabled={dictationValidated}
                style={{ width: `${minWidth}px` }}
                maxLength={maxLen}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                inputMode="text"
                spellCheck={false}
                data-1p-ignore="true"
                data-lpignore="true"
                data-form-type="other"
              />
              {trailingPunct && <span className="word-punctuation">{trailingPunct}</span>}
              {dictationValidated && !correct && (
                <div className="word-hint">
                  {normalizeWord(correctWord)}
                  {lemmaExplanation && (
                    <button
                      className="word-lemma-hint-btn"
                      onClick={() => {
                        setLemmaModalData({
                          userWord: userInput,
                          correctWord: normalizeWord(correctWord),
                          explanation: lemmaExplanation.explanation,
                          userForm: lemmaExplanation.userForm,
                          correctForm: lemmaExplanation.correctForm,
                        });
                      }}
                      title={t('comprehensionPage.dictation.viewDetails')}
                    >
                      ?
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPhaseIndicator = () => {
    const phases: { key: ComprehensionPhase; label: string }[] = [
      { key: 'understanding', label: t('comprehensionPage.phase.understanding') },
      { key: 'evaluation', label: t('comprehensionPage.phase.evaluation') },
      { key: 'dictation', label: t('comprehensionPage.phase.dictation') },
      { key: 'complete', label: t('comprehensionPage.phase.complete') },
    ];

    return (
      <div className="comprehension-phase-indicator">
        {phases.map((p, idx) => (
          <React.Fragment key={p.key}>
            <div className={`phase-step ${phase === p.key ? 'phase-step--active' : ''} ${phases.findIndex(x => x.key === phase) > idx ? 'phase-step--completed' : ''}`}>
              <span className="phase-step__number">{idx + 1}</span>
              <span className="phase-step__label">{p.label}</span>
            </div>
            {idx < phases.length - 1 && <div className="phase-step__connector" />}
          </React.Fragment>
        ))}
      </div>
    );
  };


  // ============================================
  // Loading/Error States
  // ============================================

  if (loading) {
    return <div className="comprehension-loading">{t('comprehensionPage.loading')}</div>;
  }

  if (error) {
    return <div className="comprehension-loading">{t('common:error')}: {error}</div>;
  }

  if (!currentSegment) {
    return <div className="comprehension-loading">{t('comprehensionPage.loading')}</div>;
  }

  const hasVocabulary = currentSegment.vocabulary && currentSegment.vocabulary.length > 0;
  const hasPhrases = currentSegment.phrases && currentSegment.phrases.length > 0;
  const hasVocabOrPhrases = hasVocabulary || hasPhrases;
  const hasGrammar = currentSegment.grammarPoints && currentSegment.grammarPoints.length > 0;
  const hasChunks = currentSegment.chunks && currentSegment.chunks.length > 0;
  const dictationAccuracy = calculateDictationAccuracy();
  const isLastSentence = currentIndex === segments.length - 1;

  return (
    <div className={`comprehension-page${isTranslationFocused ? ' comprehension-page--immersive' : ''}`}>
      {/* Header with Phase Indicator */}
      <div className="comprehension-header">
        <h2 className="comprehension-header__title">{t('comprehensionPage.pageTitle')}</h2>
        {phase === 'dictation' && <InputModeToggle isLetterPicker={isLetterPicker} onToggle={() => { toggleInputMode(); setLetterPickerResetKey(k => k + 1); }} />}
        {renderPhaseIndicator()}
        <div className="comprehension-header__nav">
          <button
            className="sentence-nav-btn"
            onClick={handlePreviousSentence}
            disabled={currentIndex === 0}
            aria-label={t('navigation.previousSentence')}
          >
            ◀
          </button>
          <span className="sentence-nav-counter">{currentIndex + 1} / {segments.length}</span>
          <button
            className="sentence-nav-btn"
            onClick={handleNextSentence}
            disabled={isLastSentence}
            aria-label={t('navigation.nextSentence')}
          >
            ▶
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="comprehension-content">
        <div className="comprehension-primary">
          {/* Phase 3: Dictation */}
          {phase === 'dictation' && (
            <div className="comprehension-phase comprehension-phase--dictation">
              <div className="comprehension-phase__header">
                <h3>{t('comprehensionPage.dictation.stepTitle')}</h3>
                <p>{t('comprehensionPage.dictation.stepDesc')}</p>
                <p className="comprehension-phase__shortcuts">{t('comprehensionPage.dictation.stepShortcuts')}</p>
              </div>

              <div className="comprehension-dictation__audio">
                <AudioPlayer
                  ref={audioPlayerRef}
                  audioUrl={currentSegment.audioUrl}
                  speakingWpm={courseData?.course?.speakingWpm}
                  autoNaturalSpeed={user?.autoNaturalSpeed}
                  speed={audioSpeed}
                  onSpeedChange={setAudioSpeed}
                  repeating={audioRepeating}
                  onRepeatingChange={setAudioRepeating}
                />
                {dictationValidated && (
                  <div className="comprehension-dictation__result">
                    <span className={`accuracy-score ${dictationAccuracy >= 80 ? 'accuracy-score--good' : dictationAccuracy >= 60 ? 'accuracy-score--ok' : 'accuracy-score--poor'}`}>
                      {t('comprehensionPage.dictation.accuracy', { percent: dictationAccuracy })}
                    </span>
                    {dictationAccuracy === 100 && <span className="perfect-badge">{t('comprehensionPage.dictation.perfect')}</span>}
                  </div>
                )}
              </div>

              {isLetterPicker && currentSegment && !dictationValidated ? (
                <LetterPicker
                  expectedText={currentSegment.text}
                  onComplete={handleLetterPickerComplete}
                  onErrorData={handleLpErrorData}
                  onLetterError={handleLetterError}
                  disabled={dictationValidated}
                  resetKey={letterPickerResetKey}
                />
              ) : (
                renderWordInputs()
              )}

              <div className="comprehension-phase__actions">
                {!dictationValidated ? (
                  !isLetterPicker && <button
                    className="action-button action-button--submit"
                    onClick={() => handleDictationSubmit()}
                    disabled={wordInputs.every(w => w === '')}
                  >
                    {t('comprehensionPage.dictation.checkAnswer')}
                  </button>
                ) : (
                  <button
                    className="action-button action-button--next"
                    onClick={handleComplete}
                  >
                    {t('comprehensionPage.dictation.continue')} →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Phase 1: Understanding */}
          {phase === 'understanding' && (
            <div className="comprehension-phase comprehension-phase--understanding">
              <div className="comprehension-phase__header">
                <h3>{t('comprehensionPage.understanding.stepTitle')}</h3>
                <p>{t('comprehensionPage.understanding.stepDesc')}</p>
              </div>

              {/* Sentence Card */}
              <div className="sentence-card">
                <div className="sentence-card__text">
                  {currentAnnotations.length > 0 ? (
                    <AnnotatedText text={currentSegment.text} annotations={currentAnnotations} />
                  ) : (
                    <ClickableText text={currentSegment.text} cefrLevelMap={cefrLevelMap} />
                  )}
                </div>

                <div className="sentence-card__player">
                  <AudioPlayer
                    audioUrl={currentSegment.audioUrl}
                    speakingWpm={courseData?.course?.speakingWpm}
                    autoNaturalSpeed={user?.autoNaturalSpeed}
                    speed={audioSpeed}
                    onSpeedChange={setAudioSpeed}
                    repeating={audioRepeating}
                    onRepeatingChange={setAudioRepeating}
                  />
                </div>

                {/* Grammar Point Tags */}
                {currentSegment.grammarPoints && currentSegment.grammarPoints.length > 0 && (
                  <div className="sentence-card__grammar-tags">
                    {currentSegment.grammarPoints.map((gp, idx) => {
                      const gpKey = gp.title.toLowerCase();
                      const cefrInfo = cefrGrammarMap[gpKey];
                      return (
                        <span
                          key={idx}
                          className={`grammar-tag ${cefrInfo ? `grammar-tag--cefr-${cefrInfo.cefrLevel}` : ''}`}
                          title={gp.explanation}
                        >
                          {cefrInfo && <span className="grammar-tag__level">{cefrInfo.cefrLevel}</span>}
                          <span className="grammar-tag__name">{gp.title}</span>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Dependency Graph Button */}
              {currentSegment.dependencyGraph && (
                <button className="graph-modal-trigger" onClick={() => setIsGraphModalOpen(true)}>
                  <span className="graph-modal-trigger__icon">🌳</span>
                  <span className="graph-modal-trigger__text">{t('comprehensionPage.understanding.viewSyntax')}</span>
                  <span className="graph-modal-trigger__arrow">→</span>
                </button>
              )}

              {/* Translation Input */}
              <div className="comprehension-translation">
                <label className="comprehension-translation__label">{t('comprehensionPage.understanding.translationLabel')}</label>
                <textarea
                  className="comprehension-translation__input"
                  name={`comprehension-translation-${currentIndex}`}
                  value={userTranslation}
                  onChange={(e) => setUserTranslation(e.target.value)}
                  onFocus={() => setIsTranslationFocused(true)}
                  onBlur={() => setIsTranslationFocused(false)}
                  placeholder={t('comprehensionPage.understanding.translationPlaceholder')}
                  rows={3}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  data-1p-ignore="true"
                  data-lpignore="true"
                  data-form-type="other"
                />
              </div>

              <div className="comprehension-phase__actions">
                <button
                  className="action-button action-button--submit"
                  onClick={handleTranslationSubmit}
                  disabled={!userTranslation.trim()}
                >
                  {t('comprehensionPage.understanding.submitAnswer')}
                </button>
              </div>
            </div>
          )}

          {/* Phase 2: Evaluation */}
          {phase === 'evaluation' && (
            <div className="comprehension-phase comprehension-phase--evaluation">
              <div className="comprehension-phase__header">
                <h3>{t('comprehensionPage.evaluation.stepTitle')}</h3>
              </div>

              {evaluationLoading ? (
                <div className="comprehension-evaluation__loading">
                  <div className="loading-spinner" />
                  <p>{t('comprehensionPage.evaluation.analyzing')}</p>
                </div>
              ) : evaluationResult && (
                <div className="comprehension-evaluation__result">
                  {/* Score Display */}
                  <div className={`evaluation-score ${evaluationResult.overallScore >= 90 ? 'evaluation-score--excellent' : evaluationResult.overallScore >= 60 ? 'evaluation-score--good' : 'evaluation-score--needs-work'}`}>
                    <span className="evaluation-score__value">{evaluationResult.overallScore}</span>
                    <span className="evaluation-score__label">{t('comprehensionPage.evaluation.score')}</span>
                  </div>

                  {/* Feedback */}
                  <LoopyBubble
                    state={evaluationResult.overallScore >= 90 ? 'correct' : evaluationResult.overallScore >= 60 ? 'hint' : 'error'}
                    emotion={evaluationResult.overallScore >= 90 ? 'good_job' : evaluationResult.overallScore >= 60 ? 'thinking' : 'pencil'}
                    message={evaluationResult.feedback}
                    className="evaluation-feedback evaluation-feedback--loopy"
                  >
                    {evaluationResult.vocabularyUpdated && (
                      <p className="evaluation-feedback__bonus">
                        {t('comprehensionPage.evaluation.vocabBonus', { count: evaluationResult.wordsUpdatedCount })}
                      </p>
                    )}
                  </LoopyBubble>

                  {/* Errors */}
                  {evaluationResult.errors.length > 0 && (
                    <div className="evaluation-errors">
                      <h4>{t('comprehensionPage.evaluation.attentionNeeded')}</h4>
                      {evaluationResult.errors.map((error, idx) => (
                        <div key={idx} className={`evaluation-error evaluation-error--${error.type}`}>
                          <span className="evaluation-error__type">
                            {error.type === 'vocabulary' ? t('comprehensionPage.evaluation.errorVocabulary') : error.type === 'grammar' ? t('comprehensionPage.evaluation.errorGrammar') : t('comprehensionPage.evaluation.errorStructure')}
                          </span>
                          <span className="evaluation-error__description">{error.description}</span>
                          {error.relatedWord && (
                            <span className="evaluation-error__related">{t('comprehensionPage.evaluation.relatedWord', { word: error.relatedWord })}</span>
                          )}
                          {error.relatedGrammar && (
                            <span className="evaluation-error__related">{t('comprehensionPage.evaluation.relatedGrammar', { grammar: error.relatedGrammar })}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Original English sentence */}
                  <div className="evaluation-original">
                    <span className="evaluation-original__label">{t('comprehensionPage.evaluation.originalText')}</span>
                    <div className="evaluation-original__text">
                      <ClickableText
                        text={currentSegment.text}
                        cefrLevelMap={cefrLevelMap}
                        highlightWords={evaluationResult.errors
                          .map(e => e.relatedWord)
                          .filter((w): w is string => !!w)}
                      />
                    </div>
                    <AudioPlayer
                      audioUrl={currentSegment.audioUrl}
                      speakingWpm={courseData?.course?.speakingWpm}
                      autoNaturalSpeed={user?.autoNaturalSpeed}
                      speed={audioSpeed}
                      onSpeedChange={setAudioSpeed}
                      repeating={audioRepeating}
                      onRepeatingChange={setAudioRepeating}
                    />
                  </div>

                  {/* Translation comparison */}
                  <div className="evaluation-comparison">
                    <div className="evaluation-comparison__item">
                      <span className="evaluation-comparison__label">{t('comprehensionPage.evaluation.yourTranslation')}</span>
                      <span className="evaluation-comparison__text">{userTranslation}</span>
                    </div>
                    <div className="evaluation-comparison__item evaluation-comparison__item--correct">
                      <span className="evaluation-comparison__label">{t('comprehensionPage.evaluation.referenceTranslation')}</span>
                      <span className="evaluation-comparison__text">{currentSegment.translation}</span>
                    </div>
                  </div>

                  <div className="comprehension-phase__actions">
                    <button className="action-button action-button--next" onClick={handleContinueToDictation}>
                      {t('comprehensionPage.evaluation.continue')} →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Phase 4: Complete - Overview */}
          {phase === 'complete' && (
            <div className="comprehension-phase comprehension-phase--complete">
              <div className="comprehension-overview">
                {/* Section: Original + Translation + Audio */}
                <div className="comprehension-overview__section">
                  <div className="comprehension-overview__sentence-card">
                    <div className="comprehension-overview__text">
                      {currentAnnotations.length > 0 ? (
                        <AnnotatedText text={currentSegment.text} annotations={currentAnnotations} />
                      ) : (
                        <ClickableText text={currentSegment.text} cefrLevelMap={cefrLevelMap} />
                      )}
                    </div>
                    <div className="comprehension-overview__player">
                      <AudioPlayer
                        audioUrl={currentSegment.audioUrl}
                        speakingWpm={courseData?.course?.speakingWpm}
                        autoNaturalSpeed={user?.autoNaturalSpeed}
                        speed={audioSpeed}
                        onSpeedChange={setAudioSpeed}
                        repeating={audioRepeating}
                        onRepeatingChange={setAudioRepeating}
                      />
                    </div>
                    <div className="comprehension-overview__translation">
                      {currentSegment.translation}
                    </div>
                  </div>
                </div>

                {/* Section: Vocabulary & Phrases */}
                {hasVocabOrPhrases && (
                  <div className="comprehension-overview__section">
                    <div className="comprehension-overview__section-title">{t('comprehensionPage.overview.vocabPhrases')}</div>
                    <div className="vocabulary-list">
                      {currentSegment.vocabulary?.map((item, idx) => (
                        <VocabularyCard
                          key={`overview-vocab-${idx}`}
                          word={item.word}
                          partOfSpeech={item.partOfSpeech}
                          definition={item.definition}
                          phonetic={item.phonetic}
                          audioUrl={item.audioUrl}
                          type="word"
                          cefrLevel={cefrLevelMap[item.word.toLowerCase()]}
                          onAddNote={() => handleAddNote('vocabulary', item.word, item.definition)}
                        />
                      ))}
                      {currentSegment.phrases?.map((item, idx) => (
                        <VocabularyCard
                          key={`overview-phrase-${idx}`}
                          word={item.phrase}
                          definition={item.definition}
                          phonetic={item.phonetic}
                          audioUrl={item.audioUrl}
                          type="phrase"
                          phraseType={item.phraseType}
                          cefrLevel={cefrLevelMap[item.phrase.toLowerCase()]}
                          onAddNote={() => handleAddNote('phrase', item.phrase, item.definition)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Section: Grammar Points */}
                {hasGrammar && (
                  <div className="comprehension-overview__section">
                    <div className="comprehension-overview__section-title">{t('comprehensionPage.overview.grammarPoints')}</div>
                    {currentSegment.grammarPoints?.map((point, idx) => (
                      <div key={idx} className="grammar-point">
                        <h4 className="grammar-point__title">{point.title}</h4>
                        <p className="grammar-point__explanation">{point.explanation}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Section: Dependency Graph */}
                {currentSegment.dependencyGraph && (
                  <div className="comprehension-overview__section">
                    <div className="comprehension-overview__section-title">{t('comprehensionPage.overview.syntaxStructure')}</div>
                    <div className="comprehension-overview__graph">
                      <DependencyGraph
                        data={currentSegment.dependencyGraph}
                        showControls={true}
                        showLegend={true}
                        autoPlay={false}
                      />
                    </div>
                  </div>
                )}

                {/* Section: Cultural Notes */}
                {currentSegment.culturalNotes &&
                 (currentSegment.culturalNotes.noteNative || currentSegment.culturalNotes.noteEn) && (
                  <div className="comprehension-overview__section">
                    <div className="comprehension-overview__section-title">{t('comprehensionPage.overview.culturalNotes')}</div>
                    <div className="comprehension-overview__cultural-note">
                      {currentSegment.culturalNotes.noteNative && (
                        <p className="comprehension-overview__cultural-note-text">
                          {currentSegment.culturalNotes.noteNative}
                        </p>
                      )}
                      {currentSegment.culturalNotes.noteEn && (
                        <p className="comprehension-overview__cultural-note-en">
                          {currentSegment.culturalNotes.noteEn}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <PhonicsSessionSummary errors={getSessionErrors()} />

              {/* Navigation Buttons */}
              <div className="comprehension-phase__actions">
                {isLastSentence ? (
                  <>
                    <p style={{ fontSize: '14px', color: '#666', margin: '0 0 12px 0' }}>{t('comprehensionPage.overview.allComplete')}</p>
                    <button className="action-button action-button--retry" onClick={() => { setCurrentIndex(0); resetState(); }}>
                      {t('comprehensionPage.overview.restart')}
                    </button>
                  </>
                ) : (
                  <button className="action-button action-button--next" onClick={handleNextSentence}>
                      {t('comprehensionPage.overview.nextSentence')} →
                    </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Reference Panel removed from understanding phase - shown in complete/overview instead */}
      </div>

      {/* Dependency Graph Modal */}
      {isGraphModalOpen && currentSegment.dependencyGraph && (
        <div className="graph-modal-overlay" onClick={() => setIsGraphModalOpen(false)}>
          <div className="graph-modal" onClick={(e) => e.stopPropagation()}>
            <div className="graph-modal__header">
              <h3 className="graph-modal__title">{t('comprehensionPage.syntaxModal.title')}</h3>
              <div className="graph-modal__zoom">
                <button
                  className="graph-modal__zoom-btn"
                  onClick={() => setGraphZoom(z => Math.max(0.5, z - 0.25))}
                  disabled={graphZoom <= 0.5}
                >−</button>
                <span className="graph-modal__zoom-label">{Math.round(graphZoom * 100)}%</span>
                <button
                  className="graph-modal__zoom-btn"
                  onClick={() => setGraphZoom(z => Math.min(2, z + 0.25))}
                  disabled={graphZoom >= 2}
                >+</button>
              </div>
              <button className="graph-modal__close" onClick={() => setIsGraphModalOpen(false)} aria-label={t('comprehensionPage.syntaxModal.close')}>✕</button>
            </div>
            <div className="graph-modal__sentence">{currentSegment.text}</div>
            <div className="graph-modal__content">
              <div
                className="graph-modal__graph-wrapper"
                style={
                  // Skip the transform entirely at 100% so iOS Safari doesn't
                  // build a new compositing layer that re-rasterizes SVG text
                  // at sub-pixel offsets (visible as blurry word labels).
                  graphZoom === 1
                    ? undefined
                    : { transform: `scale(${graphZoom})`, transformOrigin: 'top center' }
                }
              >
                <DependencyGraph data={currentSegment.dependencyGraph} showControls={true} showLegend={true} autoPlay={false} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lemma Explanation Modal */}
      {lemmaModalData && (
        <div className="lemma-modal-overlay" onClick={() => setLemmaModalData(null)}>
          <div className="lemma-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lemma-modal-header">
              <h3>{t('dictation.lemmaHint.modalTitle')}</h3>
              <button className="lemma-modal-close" onClick={() => setLemmaModalData(null)}>✕</button>
            </div>
            <div className="lemma-modal-content">
              <div className="lemma-modal-comparison">
                <div className="lemma-word lemma-word--user">
                  <span className="lemma-label">{t('dictation.lemmaHint.yourAnswer')}</span>
                  <span className="lemma-value">{lemmaModalData.userWord}</span>
                  {lemmaModalData.userForm && <span className="lemma-form">{t(`dictation.lemmaHint.forms.${lemmaModalData.userForm}`)}</span>}
                </div>
                <span className="lemma-arrow">→</span>
                <div className="lemma-word lemma-word--correct">
                  <span className="lemma-label">{t('dictation.lemmaHint.correctAnswer')}</span>
                  <span className="lemma-value">{lemmaModalData.correctWord}</span>
                  {lemmaModalData.correctForm && <span className="lemma-form">{t(`dictation.lemmaHint.forms.${lemmaModalData.correctForm}`)}</span>}
                </div>
              </div>
              <div className="lemma-modal-explanation">{lemmaModalData.explanation}</div>
            </div>
          </div>
        </div>
      )}
      <XpToastContainer toasts={xpToasts} />
    </div>
  );
};

export default ComprehensionPage;
